import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';

import { type DecisionRefusalProps } from './DecisionRefusal.types';

/**
 * A refused decision, drawn where the operator is looking.
 *
 * **Inside the dialog and never a toast.** "A colleague confirmed this an hour
 * ago" is news this operator has to act on, and a toast takes it away before it
 * has been read (P19b). The dialog also stays open: closing it would answer a
 * refusal by hiding the thing the refusal is about.
 *
 * **One refusal, two recoveries.** A 409 means somebody decided first and is
 * **right**, so the offer is a re-read — a retry would be refused again, and on
 * this surface the second attempt is the one that would look like a bug rather
 * than like somebody else's decision. A 404 means there is nothing to go and
 * read, so the offer is to close.
 *
 * Shared by both decision dialogs, so one refusal cannot get two slightly
 * different answers. The `useConfirm` transitions carry the same sentence
 * through their own dialog, thrown as an `Error` (`useOrderDecisions`).
 */
export function DecisionRefusal({
  message,
  isConflict,
  isGone,
  onReload,
  onClose,
}: DecisionRefusalProps) {
  const { t } = useTranslation('payments');

  if (message === null) {
    return null;
  }

  return (
    <div role="alert" className="flex flex-col gap-sm rounded-md border border-destructive p-md">
      <p className="flex items-start gap-sm text-body text-destructive">
        <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{message}</span>
      </p>

      {isConflict || isGone ? (
        <div>
          <Button
            variant="secondary"
            onClick={() => {
              if (isGone) {
                onClose();
              } else {
                onReload();
              }
            }}
          >
            {t(isGone ? 'error.close' : 'error.reread')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
